import { inject, Injectable } from '@angular/core';
import { BaseStoreFacade } from '@city/data-access';
import { Product, ProductScope, ProductService, RoutingService, StringTemplate } from '@spartacus/core';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';

export enum TemplateVariables {
  ProductName = 'productName',
  CurrentCityName = 'currentCity.name',
  CurrentCityNameGenetive = 'currentCity.nameGenitive',
  CurrentCityNameDative = 'currentCity.nameDative',
  CurrentCityNameAccusative = 'currentCity.nameAccusative',
  CurrentCityNameAblative = 'currentCity.nameAblative',
  CurrentCityNamePrepositional = 'currentCity.namePrepositional',
}

@Injectable({
  providedIn: 'root',
})
export class ResolversTextVariablesService {
  private readonly baseStoreFacade = inject(BaseStoreFacade);
  private readonly routingService = inject(RoutingService);
  private readonly productService = inject(ProductService);

  private product$: Observable<Product> = this.routingService.getRouterState().pipe(
    map((state) => state.state.params['productCode']),
    switchMap((code) => this.productService.get(code, ProductScope.DETAILS)),
  );

  private currentCity$ = this.baseStoreFacade.getActiveBaseStore();

  private mapVariablesObservables = {
    [TemplateVariables.ProductName]: this.product$.pipe(map((p) => p?.name ?? '')),
    [TemplateVariables.CurrentCityName]: this.currentCity$.pipe(map((city) => city?.name ?? '')),
    [TemplateVariables.CurrentCityNameGenetive]: this.currentCity$.pipe(map((city) => city?.nameGenitive ?? '')),
    [TemplateVariables.CurrentCityNameDative]: this.currentCity$.pipe(map((city) => city?.nameDative ?? '')),
    [TemplateVariables.CurrentCityNameAccusative]: this.currentCity$.pipe(map((city) => city?.nameAccusative ?? '')),
    [TemplateVariables.CurrentCityNameAblative]: this.currentCity$.pipe(map((city) => city?.nameAblative ?? '')),
    [TemplateVariables.CurrentCityNamePrepositional]: this.currentCity$.pipe(
      map((city) => city?.namePrepositional ?? ''),
    ),
  };

  resolveTemplateString(templateString: string): Observable<string> {
    const variables = this.extractVariables(templateString);

    const actualVariablesObservables = Object.entries(this.mapVariablesObservables).reduce(
      (acc, [variable, observable]) => {
        if (variables.includes(variable)) {
          acc[variable] = observable;
        }

        return acc;
      },
      {} as Record<string, Observable<string>>,
    );

    if (Object.keys(actualVariablesObservables).length === 0) {
      return of(templateString);
    }

    return combineLatest(actualVariablesObservables).pipe(
      map((resolvedObservables) => {
        return StringTemplate.resolve(templateString, resolvedObservables);
      }),
    );
  }

  private extractVariables(templateString: string): string[] {
    const variableRegex = /\${([^}]+)}/g;
    const variables: string[] = [];
    let match;

    while ((match = variableRegex.exec(templateString)) !== null) {
      variables.push(match[1]);
    }

    return variables;
  }
}
